import { writable, derived } from 'svelte/store';

export type ColorMode = 'light' | 'dark' | 'auto';
export type VisualTheme = string; // extensible: any theme id from config

const STORAGE_KEY_MODE = `${__STORAGE_KEY_PREFIX__}-color-mode`;
const STORAGE_KEY_THEME = `${__STORAGE_KEY_PREFIX__}-visual-theme`;

// Get initial values from localStorage or config defaults
function getInitialColorMode(): ColorMode {
  if (typeof localStorage === 'undefined') return __DEFAULT_COLOR_MODE__ as ColorMode;
  return (localStorage.getItem(STORAGE_KEY_MODE) as ColorMode) || (__DEFAULT_COLOR_MODE__ as ColorMode);
}

function getInitialVisualTheme(): VisualTheme {
  if (typeof localStorage === 'undefined') return __DEFAULT_THEME__;
  return localStorage.getItem(STORAGE_KEY_THEME) || __DEFAULT_THEME__;
}

export const colorMode = writable<ColorMode>(getInitialColorMode());
export const visualTheme = writable<VisualTheme>(getInitialVisualTheme());

// Persist changes to localStorage
colorMode.subscribe((mode) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY_MODE, mode);
  }
});

visualTheme.subscribe((theme) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY_THEME, theme);
  }
});

// Derived store for actual dark/light based on mode and system preference
export const isDark = derived(colorMode, ($mode, set) => {
  if ($mode === 'dark') {
    set(true);
  } else if ($mode === 'light') {
    set(false);
  } else {
    // Auto mode - check system preference
    if (typeof window !== 'undefined') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      set(mq.matches);

      const handler = (e: MediaQueryListEvent) => set(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    set(false);
  }
});
